import { QueryClient, QueryFunction } from "@tanstack/react-query";
import CONFIG from "./config";

// Create a query client with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 401/403/404 errors
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (message.includes('401') || message.includes('403') || message.includes('404')) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
  },
});

// Helper function to get API URL
export const getApiUrl = (url: string) => {
  if (url.startsWith('http')) {
    return url;
  }
  
  // For Vercel deployment, always use Render backend
  if (CONFIG.IS_VERCEL_DEPLOYMENT) {
    return `https://api.procurement.tectonixai.com${url}`;
  }
  
  return `${CONFIG.API_BASE_URL}${url}`;
};

// Enhanced API request function with session management
export const apiRequest = async <T = any>(url: string, options: RequestInit = {}): Promise<T> => {
  const getSessionId = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlSessionId = urlParams.get('sessionId');
      const storedSessionId = localStorage.getItem('sessionId');
      
      // Priority: URL > localStorage
      if (urlSessionId) {
        localStorage.setItem('sessionId', urlSessionId);
        return urlSessionId;
      }
      
      // Only log in verbose mode
      if (CONFIG.DEBUG_LEVEL === 'verbose') {
        console.log('🔑 Using sessionId for API:', storedSessionId?.substring(0, 10) + '...');
      }
      return storedSessionId;
    }
    return null;
  };

  let finalUrl = url;
  const sessionId = getSessionId();
  if (sessionId && !url.includes('sessionId=')) {
    const separator = url.includes('?') ? '&' : '?';
    finalUrl = `${url}${separator}sessionId=${sessionId}`;
  }

  const apiUrl = getApiUrl(finalUrl);
  
  // Only log in verbose mode
  if (CONFIG.DEBUG_LEVEL === 'verbose') {
    console.log('📡 Making API request:', apiUrl);
  }
  
  // Check if this is an admin API request and add JWT token
  const isAdminAPI = apiUrl.includes('/api/admin/');
  let headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (isAdminAPI) {
    // For admin API requests, we need to send cookies for session-based authentication
    // The backend uses session cookies (req.session.adminUser) for admin authentication
    // JWT token is optional fallback, but session cookies are primary
    const getStoredToken = () => {
      try {
        return localStorage.getItem('adminToken');
      } catch (error) {
        try {
          return sessionStorage.getItem('adminToken_session');
        } catch (sessionError) {
          return null;
        }
      }
    };
    
    const token = getStoredToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Note: We still send credentials (cookies) for admin APIs because
    // the backend authenticateAdmin middleware checks req.session.adminUser first
  }

  // 添加调试信息
  console.log('📡 API Request Debug:');
  console.log('  apiUrl:', apiUrl);
  console.log('  sessionId:', sessionId);
  console.log('  isAdminAPI:', isAdminAPI);
  console.log('  credentials:', "include"); // Always include credentials for cross-origin requests
  console.log('  headers:', headers);
  
  const response = await fetch(apiUrl, {
    headers,
    credentials: "include", // Always send cookies for session-based authentication (works with CORS)
    ...options,
  });

  if (response.status === 401) {
    // Clear auth-related queries on 401 immediately
    queryClient.removeQueries({ queryKey: ["/api/auth/user"] });
    queryClient.removeQueries({ queryKey: ["/api/auth/permissions"] });
    
    // For admin APIs, redirect to admin login
    if (isAdminAPI) {
      localStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminToken_session');
      window.location.href = '/admin/login';
      throw new Error('Unauthorized - redirecting to admin login');
    }
    queryClient.setQueryData(["/api/auth/user"], null);
    queryClient.setQueryData(["/api/auth/permissions"], null);
    
    // Try to get error details from response
    let errorMessage = `HTTP 401`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
      
      // If backend says not authenticated, redirect to OAuth immediately
      if (errorData.error === "Not authenticated" && errorData.authUrl) {
        console.log('🔐 Backend requires authentication, redirecting to OAuth immediately...');
          window.location.href = errorData.authUrl;
        return;
      }
      
      // For other 401 errors, redirect to login immediately
      if (errorMessage.includes('Authentication required') || errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        console.log('🔐 Authentication error detected, redirecting to login immediately...');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('autodeskSessionId');
        window.location.href = '/login';
        return;
      }
      
      if (errorMessage.includes("请先登录 Autodesk")) {
        window.dispatchEvent(new CustomEvent("show-autodesk-login"));
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    const error = new Error(errorMessage);
    error.status = 401; // Add status property for easier checking
    throw error;
  }

  if (!response.ok) {
    // Try to get error details from response
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  // Check if response has content before parsing JSON
  const text = await response.text();
  if (!text) {
    throw new Error("Empty response from server");
  }

  try {
    const result = JSON.parse(text);
    
    // Only log in verbose mode
    if (CONFIG.DEBUG_LEVEL === 'verbose') {
      console.log('📡 Response received:', {
        url: apiUrl,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
    }
    
    return result;
  } catch (parseError) {
    console.error('❌ JSON parse error:', parseError);
    console.error('❌ Response text:', text.substring(0, 200) + (text.length > 200 ? '...' : ''));
    
    // 检查是否是HTML错误页面
    if (text.includes('<!DOCTYPE') || text.includes('<html')) {
      throw new Error(`服务器返回了HTML错误页面而不是JSON。这通常表示后端服务器有问题。响应内容: ${text.substring(0, 100)}...`);
    }
    
    throw new Error(`Invalid JSON response: ${parseError.message}`);
  }
};

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const apiUrl = getApiUrl(queryKey[0] as string);
    const res = await fetch(apiUrl, {
      credentials: "include",
    });

    if (res.status === 401) {
      // Clear auth-related queries on 401 immediately
      queryClient.removeQueries({ queryKey: ["/api/auth/user"] });
      queryClient.removeQueries({ queryKey: ["/api/auth/permissions"] });
      queryClient.setQueryData(["/api/auth/user"], null);
      queryClient.setQueryData(["/api/auth/permissions"], null);
      
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      
      // Try to get error details from response
      let errorMessage = `HTTP 401`;
      try {
        const errorData = await res.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    if (!res.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP ${res.status}`;
      try {
        const errorData = await res.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If JSON parsing fails, use the status text
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    // Check if response has content before parsing JSON
    const text = await res.text();
    if (!text) {
      throw new Error("Empty response from server");
    }

    try {
      return JSON.parse(text);
    } catch {
      throw new Error("Invalid response format from server");
    }
  };
